import { FC, useEffect, useState } from 'react'

import { size, sumBy, uniqBy } from 'lodash'

// Components
import ConfirmationCompsWrapper from './ConfirmationCompsWrapper'
import { SelectedEventPrice } from './SelectedEventPrice'

// Redux
import { useAppSelector } from '../../../../../../store/hooks'
import { selectHorses } from '../../../../../../store/horses/horseSlice'
import {
  selectRegisterTabData,
  selectRegistration,
  selectRegistrationByDay,
} from '../../../../../../store/registration/registrationSlice'

// Helpers
import { getFloatPrice } from '../../../../../../helpers/price'
import { getRegistrations } from '../../../../../../helpers/getRegistrations'
import { getRiderHorses } from '../../../../../../helpers/riders'

import {
  HORSE_NAME_DEFAULT,
  IRegistrationByDayInterface,
} from '../../../../../../models/registrations-by-day/registrationByDay.interface'
import { IRegistrationEventTab } from '../../../../../../types/competitor_types'
import { IUserInterface } from '../../../../../../models/users/user.interface'

import { CONST } from '../../../../../../const/const'
import useEventRegister from '../../../../../../hooks/useEventRegister'

type SelectedEventListProps = {
  activeTab: IRegistrationEventTab
  setEventTab: (eventTab: IRegistrationEventTab) => void
  isSplitBill: boolean
  registrar: IUserInterface | null
  currency: string
}

const SelectedEventList: FC<SelectedEventListProps> = ({
  activeTab,
  setEventTab,
  isSplitBill,
  registrar,
  currency,
}) => {
  const { updateRegistrationByDay } = useEventRegister()

  const horses = useAppSelector(selectHorses)
  const registration = useAppSelector(selectRegistration)
  const registrationsByDay = useAppSelector(selectRegistrationByDay)
  const registerTabData = useAppSelector(selectRegisterTabData)

  const [open, setOpen] = useState(false)
  const [selectedUnits, setSelectedUnits] = useState<{
    totalItems: number
    totalPrice: number
    uniqueHorseCount: number
    uniqueMemberCount: number
  }>({ totalItems: 0, totalPrice: 0, uniqueHorseCount: 0, uniqueMemberCount: 0 })
  const [isSendBillToAllChecked, setIsSendBillToAllChecked] = useState(false)

  useEffect(() => {
    let riders = getRegistrations(registerTabData).allRegistrations
    riders = riders.filter((rider) => !rider.isDivision)

    let qualifyFee = 0
    riders.forEach((register) => {
      if (register.isQualify) qualifyFee += Number(register.qualifyFee)
    })

    const totalPrice = parseFloat(sumBy(riders, 'registrationPrice').toFixed(2))
    setSelectedUnits({
      totalItems: riders?.length,
      totalPrice: totalPrice + qualifyFee,
      uniqueHorseCount: size(uniqBy(riders, 'horseId')),
      uniqueMemberCount: size(uniqBy(riders, 'riderId')),
    })
  }, [registerTabData])

  const onRecipientChange = async (item: IRegistrationByDayInterface, isChecked: boolean) => {
    if (!item?.riderId || !registrar || !registration) return

    const registrationsByDayToUpdate = [
      {
        ...item,
        recipientId: (isChecked ? item?.riderId : registration?.userId) || null,
        recipientName: (isChecked ? item?.riderName : registration?.userName) || null,
        recipientProfilePicture:
          (isChecked ? item?.riderProfilePicture : registration?.userProfilePicture) || null,
      },
    ]

    await updateRegistrationByDay({
      registrationsByDayToUpdate,
    })
  }

  const onSendBillToAll = async (isChecked: boolean) => {
    if (!registrar || !registration) return

    setIsSendBillToAllChecked(!isSendBillToAllChecked)

    let registrationsByDayToUpdate = getRegistrations(registerTabData)?.allRegistrations

    registrationsByDayToUpdate = registrationsByDayToUpdate.map((item) => {
      return {
        ...item,
        recipientId: isChecked ? item?.riderId || null : registration?.userId || null,
        recipientName: isChecked ? item?.riderName || null : registration?.userName || null,
        recipientProfilePicture: isChecked
          ? item?.riderProfilePicture || null
          : registration?.userProfilePicture || null,
      }
    })

    await updateRegistrationByDay({
      registrationsByDayToUpdate,
    })
  }

  const registrationsByDayFiltered = registrationsByDay.filter((rider) => !rider.isDivision)

  return (
    <ConfirmationCompsWrapper
      title="Registrations"
      activeTab={activeTab}
      accordion={{ open, setOpen }}
      redirectTab={CONST.UI.REGISTER.TABS.REGISTER}
      showList={!!registrationsByDayFiltered.length}
      setEventTab={setEventTab}
      selected_units={`${selectedUnits.totalItems} ${selectedUnits.totalItems > 1 ? 'registrations' : 'registration'}, ${getFloatPrice(`${currency}${selectedUnits.totalPrice}`)}`}
      tooltipText={
        isSplitBill ? 'When splitting bills, the bill will be sent to the listed rider' : ''
      }
      isSplitBill={isSplitBill}
      isSendBillToAllChecked={isSendBillToAllChecked}
      onSendBillToAll={onSendBillToAll}
    >
      {registrationsByDayFiltered.map((item, index) => (
        <div
          key={index}
          className="w-full flex flex-wrap flex-col sm:flex-row gap-3 sm:items-center sm:justify-between p-4 hover:bg-gray-50 transition-colors border-b border-gray-100"
        >
          <div>
            <p className="font-medium text-gray-900">{item?.registrationByDayName}</p>
            <p className="text-sm text-gray-600">
              Rider: {item.riderName || 'N/A'} | Horse:{' '}
              {getRiderHorses(horses ?? [], item.horsesSelected || [])
                ?.map((horse) => horse.horseName)
                .join(', ') || HORSE_NAME_DEFAULT}
            </p>
          </div>
          <SelectedEventPrice
            onRecipientChange={(state) => onRecipientChange(item, state)}
            recipientId={item?.recipientId || ''}
            ownerId={item?.riderId || ''}
            price={getFloatPrice(`${currency}${item.registrationPrice}`)}
            isQualify={item.isQualify}
            qualifyFee={getFloatPrice(`${currency}${item.qualifyFee}`)}
            qualifyFeeName={item.qualifyFeeName}
            isSplitBill={isSplitBill}
          />
        </div>
      ))}
    </ConfirmationCompsWrapper>
  )
}

export default SelectedEventList
